import {Trans} from '@lingui/macro';
import {compact} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {buildErrorCodeMap} from '../../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {Accordion} from '../../../../../../../../../src/components/common/accordion/accordion';
import {AccordionSectionData} from '../../../../../../../../../src/components/common/accordion/accordionSection';
import {
  CrmSyncErrorModel,
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncSourcesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {fontWeights} from '../../../../../../../../shared/ui/src/styles/fontStyles';
import {CrmSyncsErrorsList} from './crmSyncsErrorsList';

/*
 * Props.
 */

interface CrmSyncsErrorsProps {
  source: CrmSyncSourcesEnum;
  accountSyncErrors: ReadonlyArray<CrmSyncErrorModel>;
  cardSyncErrors: ReadonlyArray<CrmSyncErrorModel>;
  externalBaseUrl?: string;
}

/*
 * Styles.
 */

const StyledSectionTitleDiv = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};
  font-size: 16px;
  gap: 7px;
`;

const StyledCountBadgeDiv = styled.div`
  font-size: 10px;
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
  background: ${(p) => p.theme.greys.shade30};
  border-radius: 50%;
  text-align: center;
  line-height: 16px;
  height: 16px;
  width: 16px;
`;

const StyledErrorsPageWrapperDiv = styled.div`
  margin-left: -16px; // offset the accordion padding
`;

/*
 * Component.
 */

export const CrmSyncsErrors: FC<CrmSyncsErrorsProps> = (props) => (
  <StyledErrorsPageWrapperDiv>
    <Accordion allowMultiSelect disableBorders sections={buildAccordionSections(props)} />
  </StyledErrorsPageWrapperDiv>
);

/*
 * Helpers.
 */

function buildAccordionSections(props: CrmSyncsErrorsProps): Array<AccordionSectionData> {
  return compact([buildAccountsSection(props), buildCardsSection(props)]);
}

function buildCardsSection(props: CrmSyncsErrorsProps): AccordionSectionData | null {
  if (props.cardSyncErrors.length === 0) {
    return null;
  }
  const errorCodeMap = buildErrorCodeMap(props.cardSyncErrors);
  const errorCodes = Object.keys(errorCodeMap);
  return {
    id: CrmSyncIntegrationResourceTypesEnum.CARD,
    title: (
      <StyledSectionTitleDiv>
        <Trans id="crm-syncs-page-errors-contact-section-title">Contact sync errors</Trans>
        <StyledCountBadgeDiv>{errorCodes.length}</StyledCountBadgeDiv>
      </StyledSectionTitleDiv>
    ),
    isExpandedByDefault: true,
    sectionPadding: 0,
    content: (
      <CrmSyncsErrorsList
        source={props.source}
        resourceType={CrmSyncIntegrationResourceTypesEnum.CARD}
        errorCodeMap={errorCodeMap}
        externalBaseUrl={props.externalBaseUrl}
      />
    ),
  };
}

function buildAccountsSection(props: CrmSyncsErrorsProps): AccordionSectionData | null {
  if (props.accountSyncErrors.length === 0) {
    return null;
  }
  const errorCodeMap = buildErrorCodeMap(props.accountSyncErrors);
  const errorCodes = Object.keys(errorCodeMap);

  return {
    id: CrmSyncIntegrationResourceTypesEnum.ACCOUNT,
    title: (
      <StyledSectionTitleDiv>
        <Trans id="crm-syncs-page-errors-account-section-title">Account sync errors</Trans>
        <StyledCountBadgeDiv>{errorCodes.length}</StyledCountBadgeDiv>
      </StyledSectionTitleDiv>
    ),
    isExpandedByDefault: true,
    sectionPadding: 0,
    content: (
      <CrmSyncsErrorsList
        source={props.source}
        resourceType={CrmSyncIntegrationResourceTypesEnum.ACCOUNT}
        errorCodeMap={errorCodeMap}
        externalBaseUrl={props.externalBaseUrl}
      />
    ),
  };
}
